import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";

import { BRAND } from "../brand/brand";
import { authenticationAtom } from "../State";
import { useCustomerTheme } from "../Theme";
import { userAPI } from "../UserAPI";

/** Wordmark of the brand this host presents as — see src/brand/brand.ts. */
export const DEFAULT_LOGO = BRAND.logo;

/**
 * Logo des aktiven Mandanten — extrahiert aus dem Header, damit Sidebar und
 * Header dieselbe Auflösung teilen. Liefert das Kundenlogo (Theme bzw. die
 * fest verdrahteten Altkunden) und als Fallback das Logo der Marke, unter der
 * das Portal aufgerufen wurde.
 *
 * Auf einem markengebundenen Host (FireIQ) entfällt die Mandantenauflösung
 * ganz: dort zeigt das Portal immer die Marke des Hosts, egal zu welchem
 * Mandanten die angemeldete Nutzerin gehört.
 */
export function useCustomerLogo(): { logo: string; isFallback: boolean } {
  const [searchParams] = useSearchParams();
  const [authentication] = useAtom(authenticationAtom) as any;
  const { theme, themeLoading } = useCustomerTheme();

  const resolve = (): string => {
    if (BRAND.defaultTenant) {
      return DEFAULT_LOGO;
    }

    // A ?tenant=<id> URL param signals a branded entry point. Prefer that
    // tenant's theme logo regardless of who's authenticated, so a deep link
    // like /chatbot?tenant=acme always shows Acme's branding.
    const tenantParam = searchParams.get("tenant");
    if (tenantParam && tenantParam !== "null") {
      // themeAtom is persisted; while tenant theme is being fetched we may
      // still have a previous tenant's logoURL in storage. Avoid rendering any
      // tenant logo until this navigation's tenant theme has finished loading.
      if (!themeLoading && theme?.logoURL) {
        return userAPI.getResourceURL(theme.logoURL);
      }

      return DEFAULT_LOGO;
    }

    if (!authentication?.customer) {
      return DEFAULT_LOGO;
    }

    switch (authentication.customer) {
      case "S8QW-G8R2-9QLC": // The Real Insight
        return DEFAULT_LOGO;
      case "4712": // Fire IQ
        return "/customers/fire-iq.png";
      case "4713": // Mondrian
        return "/customers/mondrian.png";
      case "4714": // Pastor
        return "/customers/pastor.png";
      default:
        return theme?.logoURL ? userAPI.getResourceURL(theme.logoURL) : DEFAULT_LOGO;
    }
  };

  const logo = resolve();
  return { logo, isFallback: logo === DEFAULT_LOGO };
}
